import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Key, Eye, EyeOff } from "lucide-react";

interface PasswordResetFormProps {
  onSuccess?: () => void;
  title?: string;
  showCard?: boolean;
  isForForgotPassword?: boolean;
}

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What was your mother's maiden name?",
  "What was the name of your first school?",
  "What was your childhood nickname?",
  "What street did you live on as a child?",
  "What was the make of your first car?",
  "What is your favorite movie?",
  "What was the name of your best friend in high school?",
  "What is your favorite food?"
];

export const PasswordResetForm = ({ 
  onSuccess, 
  title = "Reset Password",
  showCard = true,
  isForForgotPassword = false
}: PasswordResetFormProps) => {
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = verification, 2 = password reset
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const { toast } = useToast();

  const handleVerifySecurityQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Temporary message for beta launch
    toast({
      title: "Password Reset Currently Unavailable",
      description: "Email system is being configured. Please contact support at [your-email] for password assistance during beta testing.",
      variant: "destructive",
    });
    return;
    if (!email || !answer.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-security-question', {
        body: {
          email: email.trim(),
          answer: answer.trim()
        }
      });

      if (error) throw error;

      if (data?.success) {
        setQuestion(data.question);
        setVerifiedEmail(email);
        setStep(2);
        toast({
          title: "Verification Successful",
          description: "Please enter your new password.",
        });
      } else {
        throw new Error(data?.error || "Verification failed");
      }
    } catch (error: any) {
      console.error('Error verifying security question:', error);
      toast({
        title: "Error",
        description: error.message || "Verification failed. Please check your email and security answer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast({
        title: "Error",
        description: "Please fill in all password fields.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('reset-password-with-security', {
        body: {
          email: verifiedEmail,
          question,
          answer: answer.trim(),
          newPassword
        }
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Success",
          description: "Password reset successfully! You can now log in with your new password.",
        });

        // Reset form
        setEmail("");
        setQuestion("");
        setAnswer("");
        setNewPassword("");
        setConfirmPassword("");
        setStep(1);
        setVerifiedEmail("");
        
        onSuccess?.();
      } else {
        throw new Error(data?.error || "Failed to reset password");
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const stepOneContent = (
    <form onSubmit={handleVerifySecurityQuestion} className="space-y-4">
      {isForForgotPassword && (
        <div>
          <Label htmlFor="reset-email">Email Address</Label>
          <Input
            id="reset-email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      )}
      
      <div>
        <Label htmlFor="reset-answer">Security Answer</Label>
        <Input
          id="reset-answer"
          type="text"
          placeholder="Enter your security answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          required
          maxLength={100}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Answer the security question you set up during account creation.
        </p>
      </div>

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? "Verifying..." : "Verify Security Answer"}
      </Button>
    </form>
  );

  const stepTwoContent = (
    <form onSubmit={handlePasswordReset} className="space-y-4">
      <div className="p-3 bg-muted rounded-md">
        <p className="text-sm text-muted-foreground">
          <strong>Security Question:</strong> {question}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          <strong>Email:</strong> {verifiedEmail}
        </p>
      </div>

      <div>
        <Label htmlFor="new-password">New Password</Label>
        <div className="relative">
          <Input
            id="new-password"
            type={showPassword ? "text" : "password"}
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div>
        <Label htmlFor="confirm-password">Confirm New Password</Label>
        <Input
          id="confirm-password"
          type={showPassword ? "text" : "password"}
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
        />
      </div>

      <div className="flex gap-2">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            setStep(1);
            setNewPassword("");
            setConfirmPassword("");
          }}
          className="flex-1"
        >
          Back
        </Button>
        <Button type="submit" disabled={loading} className="flex-1">
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </div>
    </form>
  );

  const content = step === 1 ? stepOneContent : stepTwoContent;

  if (!showCard) {
    return content;
  }

  return (
    <Card className="obsidian-card">
      <CardHeader>
        <CardTitle className="text-energy flex items-center gap-2">
          <Key className="h-5 w-5" />
          {title} {step === 2 && "- Set New Password"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};